import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, auditApi, type AuditAction } from '../api/client';

interface FileHistoryPopoverProps {
  path: string;
}

const ACTION_LABEL: Record<AuditAction, string> = {
  view: 'Viewed',
  download: 'Downloaded',
  pdf: 'Downloaded PDF',
  proxy_view: 'Viewed',
};

/**
 * Compact "Who viewed this?" button that opens a popover listing the most
 * recent access events for the currently visible document. Scoped by the
 * server to the calling user's customer, so any portal user is allowed to
 * see it for files in their own portal.
 */
export function FileHistoryPopover({ path }: FileHistoryPopoverProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close when the user clicks outside the popover surface. We attach the
  // listener only while the popover is open so a closed popover doesn't keep
  // a document-level handler around.
  useEffect(() => {
    if (!open) return;
    function onMouseDown(event: MouseEvent): void {
      if (!containerRef.current) return;
      if (containerRef.current.contains(event.target as Node)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const historyQuery = useQuery({
    queryKey: ['audit-history', path],
    queryFn: () => auditApi.history(path, 20),
    enabled: open && !!path,
    staleTime: 15_000,
  });
  // Read the cached `me` so we know which slug is the "active" tenant for
  // this popover. Used to decide when to surface a "via {homeSlug}" tag
  // next to actors whose home customer differs from where they're acting.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
  const activeCustomerSlug = meQuery.data?.customer.slug;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1.5 rounded-md border border-wc-muted/30 px-3 py-1.5 text-xs font-medium text-wc-muted-strong transition-colors hover:border-wc-blue hover:text-wc-blue"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="View history for this document"
      >
        <HistoryIcon />
        History
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="File view history"
          className="absolute right-0 top-full z-20 mt-2 w-96 max-w-[90vw] overflow-hidden rounded-lg border border-wc-muted/20 bg-wc-white shadow-xl"
        >
          <div className="border-b border-wc-muted/15 bg-wc-gray/60 px-4 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-wc-muted-strong">
              Recent activity
            </p>
            <p className="mt-0.5 truncate text-xs text-wc-black" title={path}>
              {path}
            </p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {historyQuery.isLoading ? (
              <p className="px-4 py-6 text-center text-xs text-wc-muted">Loading…</p>
            ) : historyQuery.isError ? (
              <p className="px-4 py-6 text-center text-xs text-wc-red">
                Failed to load history.
              </p>
            ) : !historyQuery.data || historyQuery.data.items.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-wc-muted">
                No views recorded yet.
              </p>
            ) : (
              <ul className="divide-y divide-wc-muted/15">
                {historyQuery.data.items.map((item) => {
                  const isVia =
                    !!activeCustomerSlug &&
                    !!item.homeCustomerSlug &&
                    item.homeCustomerSlug !== activeCustomerSlug;
                  return (
                    <li key={item.id} className="px-4 py-2.5 text-xs">
                      <p className="flex items-center gap-1.5 font-medium text-wc-black">
                        <span>{item.displayName ?? item.email}</span>
                        {isVia && (
                          <span
                            className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-800"
                            title={`Acting from home customer "${item.homeCustomerSlug}"`}
                          >
                            via {item.homeCustomerSlug}
                          </span>
                        )}
                      </p>
                      {item.displayName && (
                        <p className="text-wc-muted-strong">{item.email}</p>
                      )}
                      <p className="mt-0.5 flex items-center gap-2 text-wc-muted">
                        <span>{ACTION_LABEL[item.action]}</span>
                        <span aria-hidden>·</span>
                        <time dateTime={item.createdAt}>{formatRelative(item.createdAt)}</time>
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelative(iso: string): string {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return iso;
  const deltaSec = Math.round((Date.now() - target) / 1000);
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.round(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.round(deltaSec / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

function HistoryIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <polyline points="3 4 3 10 9 10" />
      <polyline points="12 7 12 12 16 14" />
    </svg>
  );
}
